import { frameLayout } from './fit.js';
import { cellOutline } from './hit-test.js';
import { byOrder, quadToStage } from './layout.js';
import { gridCells, gridTotal } from '@signa/shared';
/**
 * 文言 1 文字ぶんの見積もり幅(表示ピクセル)。**全角で見る。**
 *
 * 実際の幅はフォントと字種で変わるが、**測るには DOM に置いて計るしかない** ——
 * 輪郭を組むたびにそれをやると重い。**安全側(全角)に倒して見積もる。**
 */
const HINT_CHAR_WIDTH = 13;
/**
 * 文言の左右に残す余白(両側の合計)。枠線・写真の端・隣の文言に
 * ぎりぎりまで文字が寄らないようにする。
 */
const HINT_PADDING = 16;
/** 文言を置くのに要る高さ。1 行ぶん + 上下の余白。 */
const HINT_MIN_HEIGHT = 32;
/**
 * 文言 1 つが占める矩形の見積もり(表示ピクセル)。
 *
 * **収まるかの判定(`fitsHint`)と、重なり・寄せの判定(`placeHints`)で
 * 同じ寸法を使う** —— 別々に持つと、片方だけ変えたときに食い違う
 * (#208 で閾値と文言を別々に持って踏んだのと同じ問題)。
 */
function hintSize(text) {
    return { width: text.length * HINT_CHAR_WIDTH + HINT_PADDING, height: HINT_MIN_HEIGHT };
}
/** 多角形の外接矩形。 */
function boundingBox(points) {
    const xs = points.map(([x]) => x);
    const ys = points.map(([, y]) => y);
    return {
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
    };
}
/**
 * このセルに `text` が**収まるか**。
 *
 * **出すかどうかの判定ではない**(#261)。収まらないセルにも文言は出す ——
 * ここで決まるのは**優先して必ず置くか**で、収まらない文言は
 * [`placeHints`](#placeHints) が**他の文言と重ならないときだけ**置く。
 * #208 の時点では「収まらなければ枠だけ」だった。
 *
 * ## なぜ文言を受け取るのか
 *
 * **固定の閾値にすると、文言を変えたときに黙って溢れる。** 実際、最初は
 * 「120px 以上なら出す」と決め打っていたが、**文言が 13px × 10 文字 = 130px**
 * で**閾値のほうが狭かった** —— 判定を通ったセルで文字がはみ出す。
 *
 * **長さを見れば、文言を変えても判定がついてくる。**
 *
 * ## なぜ外接矩形で測るのか
 *
 * セルは台形になりうるので「幅」「高さ」が一意に決まらない ——
 * 外接矩形なら**どんな形でも同じ尺度**で測れる。**安全側ではない**
 * (台形の狭いほうでは溢れうる)が、そこまで厳密にすると
 * ほとんどのセルが「収まらない」扱いになり、文言が間引かれやすくなる。
 */
export function fitsHint(points, text) {
    if (points.length === 0 || text === '')
        return false;
    const box = boundingBox(points);
    const size = hintSize(text);
    return box.width >= size.width && box.height >= size.height;
}
/**
 * **枠の素材種別に応じた案内文言を選ぶ**(#240 / [ADR-0038](../../../docs/adr/0038-frame-source-kind-hint.md))。
 *
 * 選ぶのを描画コアに寄せているのは、**呼び出し側が枠ごとに文言を写して回らずに済む**ため。
 * 種別が未指定(`null` / `undefined`)なら種類を言わない文言に落ちる。
 */
export function resolveCellHint(hint, kind) {
    return { drop: kind == null ? hint.drop.any : hint.drop[kind], touch: hint.touch };
}
/**
 * 寸法を見積もる文言。**長いほうを採る**(#239)。
 *
 * **入力方式で出る文言が変わる**(CSS が切り替える)ので、**どちらが出ても**
 * 同じ判定になるよう長いほうで測る。収まるか(`fitsHint`)も、重なりと
 * 写真の端での寄せ(`placeHints`)も、これで見積もる ——
 * **片方だけで測ると、もう片方が枠や隣の文言にかかる。**
 *
 * いま上限を握っているのは**タッチ向け**(「PC で試せます」= 8 文字 = 120px)——
 * D&D 向けの文言をこれより短くしても、**収まる扱いのセルは広がらない。**
 */
export function longestHintText(hint) {
    return hint.drop.length >= hint.touch.length ? hint.drop : hint.touch;
}
/**
 * 多角形の**面積の重心**。
 *
 * **頂点の平均ではない。** 台形のセル(遠近のある写真)では、頂点の平均は
 * **狭いほうの辺に引っ張られる** —— 面積の重心のほうが「真ん中に見える」
 * 位置になる。
 *
 * 面積が 0(潰れた多角形)なら頂点の平均に落とす。
 */
export function centroidOf(points) {
    const average = () => [
        points.reduce((sum, [x]) => sum + x, 0) / points.length,
        points.reduce((sum, [, y]) => sum + y, 0) / points.length,
    ];
    if (points.length < 3)
        return average();
    let twiceArea = 0;
    let x = 0;
    let y = 0;
    for (let i = 0; i < points.length; i += 1) {
        const [x0, y0] = points[i] ?? [0, 0];
        const [x1, y1] = points[(i + 1) % points.length] ?? [0, 0];
        const cross = x0 * y1 - x1 * y0;
        twiceArea += cross;
        x += (x0 + x1) * cross;
        y += (y0 + y1) * cross;
    }
    if (twiceArea === 0)
        return average();
    return [x / (3 * twiceArea), y / (3 * twiceArea)];
}
/**
 * 空のセルの輪郭を、**重なり順**(`order` が小さいほど先)で返す。
 *
 * `hasSource` は「そのセルに素材が載っているか」を答える。**描画コアの台帳
 * (`upload-registry`)が知っている**ので、呼び出し側から状態を渡す必要はない。
 *
 * **潰れたクアッドのセルは飛ばす。** 描けないものを描こうとしない。
 */
export function emptyCellOutlines(frames, transform, hasSource) {
    const shapes = [];
    for (const frame of byOrder(frames)) {
        const stageQuad = quadToStage(frame.quad, transform);
        // **未分割の枠はセル 1 つ**として返る(`fit.ts` の `frameLayout`)。
        const layout = frameLayout(stageQuad, frame.grid);
        if (layout === null)
            continue;
        const distances = centerDistances(frame.grid);
        for (const cell of layout.cells) {
            if (hasSource(frame.id, cell.index))
                continue;
            const points = cellOutline(stageQuad, frame.grid, cell.index);
            if (points === null)
                continue;
            shapes.push({
                frameId: frame.id,
                cellIndex: cell.index,
                points,
                centerDistance: distances.get(cell.index) ?? 0,
            });
        }
    }
    return shapes;
}
/**
 * 盤面の中心から各セルの中心までの距離。**格子の単位**(`grid` の項の値)で測る(#261)。
 *
 * ## なぜ表示ピクセルで測らないのか
 *
 * **窓の幅を変えるたびに文言が別のセルへ飛ぶ**(#261 の最終レビューで実測)。
 * 遠近の少しある盤面では、左右のセルの距離が表示ピクセルで**ほぼ同じ**になる ——
 * 丸めて比べると、同じ整数に丸まる幅と別々に丸まる幅とで順が入れ替わる。
 * さらに遠近が強いと、面積の重心に最も近いのが真ん中のセルではなくなる。
 *
 * **格子の単位なら表示の大きさにも遠近にも左右されない。** 対称な位置のセルは
 * 厳密に同じ値になり(整数の項なら中心からの差も誤差なく求まる)、
 * そのときは [`placeHints`](#placeHints) がセル番号の小さいほうを採る。
 */
function centerDistances(grid) {
    // **分割していない枠はセル 1 つ**(`fit.ts` の `frameLayout` と同じ扱い)。
    if (grid == null)
        return new Map([[0, 0]]);
    const total = gridTotal(grid);
    const centerX = total.width / 2;
    const centerY = total.height / 2;
    return new Map(gridCells(grid).map((cell) => [
        cell.index,
        Math.hypot((cell.left + cell.right) / 2 - centerX, (cell.top + cell.bottom) / 2 - centerY),
    ]));
}
/**
 * **案内の文言を置く位置を決める**(#261)。戻り値は `shapes` と同じ並びで、
 * **`null` は文言を出さない**(枠だけ描く)。
 *
 * `textOf` は寸法を見積もる文言を返す(PC 向けとタッチ向けの**長いほう** ——
 * [`longestHintText`](#longestHintText))。
 *
 * ## なぜはみ出させるのか
 *
 * #208 では**収まらないセルに文言を出さなかった**(溢れるより黙る)。ところが
 * **想定より小さい盤面では破線の枠だけになり、盤面そのものが見つけにくい** ——
 * 小さい盤面ほど見つけにくいのに、小さい盤面ほど案内が消えていた。
 * **案内の役目は発見**なので、収まらなくても出す。
 *
 * ## 規則
 *
 * | 順 | 対象 | 置き方 |
 * |---|---|---|
 * | 1 | **収まるセル**([`fitsHint`](#fitsHint)) | **必ず置く**(#208 からの見え方を変えない)。互いの重なりは見ない |
 * | 2 | **はみ出すセル** | **置いた文言と重ならないときだけ**置く。重なり順(`order` が小さい = 下の枠から)→ 盤面の中心に近い順 → セル番号の順 |
 *
 * 位置はどちらも**セルの面積の重心**。**写真の外で切れる軸だけ内側へ寄せ**、
 * 重なりは**寄せた後の位置**で見る。
 *
 * **写真にかからないセルには置かない**(外接矩形が写真と交わらない)。頂点は
 * キャンバスの外に出してよいので、写真の外に丸ごと出たセルもありうる ——
 * 寄せると**枠が見えないのに文言だけが端に出る。**
 *
 * ## なぜ重ねないのか
 *
 * **分割した盤面の小さいセル**では、はみ出した文言が隣のセルの文言と重なる ——
 * 同じ文言が何重にも重なると**どれも読めない。** 1 つ読めれば盤面は見つかり、
 * 残りのセルは破線の枠が示す。**埋めると次の文言が出る。**
 *
 * ## なぜ中心に近いセルからなのか
 *
 * セル番号の順だと**左上のセル**に文言が寄り、盤面から外れて見える ——
 * 中心から置けば、3 × 3 の小さい盤面なら**真ん中のセル**に 1 つ置かれる。
 * 距離は**格子の単位**で測る(`CellOutlineShape.centerDistance`)ので、
 * 窓の幅を変えても遠近があっても**同じセルが選ばれる。**
 */
export function placeHints(shapes, textOf, bounds) {
    const anchors = shapes.map(() => null);
    const placed = [];
    /** 重なり順。`shapes` は `order` の順に並んでいる(`emptyCellOutlines`)。 */
    const frameRank = new Map();
    for (const shape of shapes) {
        if (!frameRank.has(shape.frameId))
            frameRank.set(shape.frameId, frameRank.size);
    }
    const candidates = shapes.flatMap((shape, index) => {
        const text = textOf(shape);
        // 描けないもの・言うことの無いものは置かない(`fitsHint` と同じ扱い)。
        if (shape.points.length === 0 || text === '')
            return [];
        // 見えないセルにも置かない(寄せると文言だけが写真の端に出る)。
        if (!touchesBounds(shape.points, bounds))
            return [];
        const at = keepInside(centroidOf(shape.points), text, bounds);
        return [
            {
                index,
                cellIndex: shape.cellIndex,
                rank: frameRank.get(shape.frameId) ?? 0,
                distance: shape.centerDistance,
                fits: fitsHint(shape.points, text),
                at,
                box: hintBox(at, text),
            },
        ];
    });
    const place = (candidate) => {
        anchors[candidate.index] = candidate.at;
        placed.push(candidate.box);
    };
    for (const candidate of candidates) {
        if (candidate.fits)
            place(candidate);
    }
    const overflowing = candidates
        .filter((candidate) => !candidate.fits)
        .sort((a, b) => a.rank - b.rank || a.distance - b.distance || a.cellIndex - b.cellIndex);
    for (const candidate of overflowing) {
        if (placed.some((other) => overlaps(other, candidate.box)))
            continue;
        place(candidate);
    }
    return anchors;
}
/** `at` を中心にした見積もり矩形。文言は `text-anchor: middle` / `dominant-baseline: central` で置く(`stage.ts`)。 */
function hintBox([x, y], text) {
    const { width, height } = hintSize(text);
    return { left: x - width / 2, top: y - height / 2, right: x + width / 2, bottom: y + height / 2 };
}
/**
 * **接するだけなら重なりではない。** 矩形は余白(`HINT_PADDING`)を含むので、
 * 接していても字どうしは離れている。
 */
function overlaps(a, b) {
    return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}
/**
 * 見積もり矩形が範囲からはみ出す軸だけ、**内側へ寄せる**(#261)。
 *
 * **写真の外は切れる**(ステージは `overflow: hidden`、SVG も自分の範囲で切る)。
 * 端にある小さい盤面の文言が半分切れると、はみ出させて出した意味がない。
 */
function keepInside([x, y], text, bounds) {
    const { width, height } = hintSize(text);
    return [clampAxis(x, width / 2, bounds.width), clampAxis(y, height / 2, bounds.height)];
}
/**
 * 1 軸ぶんの寄せ。**範囲より大きいなら中央** —— どちらへ寄せても切れるので、
 * 両側を均等に切る。
 */
function clampAxis(value, half, length) {
    if (half * 2 >= length)
        return length / 2;
    return Math.min(Math.max(value, half), length - half);
}
/**
 * セルが写真に**少しでもかかっているか**(外接矩形で見る)。
 * **接するだけ・表示サイズ 0 はかからない**扱い。
 */
function touchesBounds(points, bounds) {
    const xs = points.map(([x]) => x);
    const ys = points.map(([, y]) => y);
    return (Math.min(...xs) < bounds.width &&
        Math.max(...xs) > 0 &&
        Math.min(...ys) < bounds.height &&
        Math.max(...ys) > 0);
}
//# sourceMappingURL=outlines.js.map